/**
 * Layout component that queries for data
 * with Gatsby's useStaticQuery component
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import { StyleSheetManager, ThemeProvider } from 'styled-components';
import { useIntl } from 'gatsby-plugin-react-intl';
import isPropValid from '@emotion/is-prop-valid';

import Header from '../Header';
import Footer from '../Footer';
import { Promotion } from '../../Providers/Promotion';
import RefreshContent from '../../Custom/RefreshContent';
import theme, { GlobalStyles } from '../../../themes';
import { useFeatureFlag } from '../../../hooks';
import { CoreContext } from '../../Providers/Core';
import { getAnonyChannel } from '../../Providers/Core/Services/CoreEffects';

import { Main } from './styles';
import 'aos/dist/aos.css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/bundle';

// TODO: Remove/Refactor once we figure out where Dark Mode will be comimg from
const darkMode = false;

// This implements the default behavior from styled-components v5
const shouldForwardProp = (propName, target) => {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
};

const Layout = ({
  location = null,
  children,
  header,
  footer,
  promoCode = null,
  redirectPromoCode = null,
  languageToggle = null,
  banner = [],
  contentType = null,
}) => {
  const data = useStaticQuery(query);
  const intl = useIntl();

  const ariaMsgTab =
    intl.locale === 'en'
      ? 'Opens in a new tab'
      : 'S’affiche dans un nouvel onglet';

  // Function to set focus to main content with "Skip to Content" is clicked
  const skipToContentFocus = e => {
    e.preventDefault();
    if (typeof window !== 'undefined') {
      window.location.href = '#main';
    }
  };

  // Can we mive thhis into gatsby browser to redirect to the correct page with promo code?
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (redirectPromoCode) {
        const { redirectedPromoCode, redirectedUrl } = redirectPromoCode;
        if (window.location.href.includes('promoCode')) {
          const params = new URL(document.location).searchParams;
          const queryRafParam = params.get('promoCode');
          if (queryRafParam === redirectedPromoCode) {
            window.location.replace(redirectedUrl);
          }
        }
      }
    }
  }, []);

  const PSITE_SAVINGS_INTEREST_API = useFeatureFlag(
    'PSITE_SAVINGS_INTEREST_API',
  );

  const { core, updateCore } = useContext(CoreContext);

  useEffect(() => {
    if (typeof window !== 'undefined' || !core.rates.baseInterestRate) {
      if (!core.client_id || !core.rates.baseInterestRate) {
        getAnonyChannel(core, updateCore, PSITE_SAVINGS_INTEREST_API);
      }
    }
  }, []);

  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        {process.env.ENABLE_GATSBY_REFRESH_ENDPOINT === false && (
          <RefreshContent />
        )}
        <Promotion
          value={promoCode}
          location={location}
          contentType={contentType}
        >
          {header && (
            <Header
              data={header}
              banner={banner}
              siteTitle={data.site.siteMetadata.title}
              type="fixed"
              skipToContentFocus={skipToContentFocus}
              contentType={contentType}
              darkMode={darkMode && darkMode}
            />
          )}
          <Main id="main">{children}</Main>
          <div hidden>
            <span id="new-window-0">{ariaMsgTab}</span>
          </div>
          {footer && <Footer data={footer} languageToggle={languageToggle} />}
        </Promotion>
      </ThemeProvider>
    </StyleSheetManager>
  );
};

Layout.propTypes = {
  location: PropTypes.shape(),
  children: PropTypes.node.isRequired,
  header: PropTypes.shape().isRequired,
  footer: PropTypes.shape().isRequired,
  redirectPromoCode: PropTypes.shape(),
  promoCode: PropTypes.string,
  languageToggle: PropTypes.bool,
  banner: PropTypes.arrayOf(PropTypes.shape()),
  contentType: PropTypes.string,
};

const query = graphql`
  query SiteTitleQuery {
    site {
      siteMetadata {
        title
      }
    }
  }
`;

export default Layout;
